import { z } from "zod";
import { BearingDeg, BoundingBox, LatLon, SpeedKnots } from "./geometry.js";

/**
 * Beaufort-aligned sea state, 0 (calm) to 9 (phenomenal).
 */
export const SeaState = z
  .number()
  .int()
  .gte(0)
  .lte(9) as unknown as z.ZodType<SeaState>;
export type SeaState = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export const TidePhase = z.enum(["springs", "neaps", "mid"]);
export type TidePhase = z.infer<typeof TidePhase>;

export const RainCell = z.object({
  id: z.string().min(1),
  centre: LatLon,
  radiusNm: z.number().positive(),
  intensity: z.number().int().gte(1).lte(5),
  driftCog: BearingDeg,
  driftSpeedKnots: SpeedKnots,
});
export type RainCell = z.infer<typeof RainCell>;

export const EnvironmentState = z.object({
  seaState: SeaState,
  windFromDeg: BearingDeg,
  windSpeedKnots: SpeedKnots,
  visibilityNm: z.number().positive(),
  rainCells: z.array(RainCell),
  tidePhase: TidePhase,
});
export type EnvironmentState = z.infer<typeof EnvironmentState>;

export const VesselType = z.enum([
  "ferry",
  "container",
  "fishing",
  "yacht-sail",
  "yacht-power",
  "tug",
  "naval",
  "rib",
  "buoy",
  /** High-speed catamaran (Wightlink FastCat / Red Funnel Red Jet).
   *  Carries the standard power-driven lights plus an all-round
   *  flashing yellow above per COLREG Rule 23(b) — high-speed craft. */
  "fastcat",
  /** Air-cushion vessel. Same yellow flashing rule. */
  "hovercraft",
  /** Pilot vessel on duty. Carries white-over-red all-round in
   *  addition to sidelights/sternlight per Rule 29. */
  "pilot",
  /** Dredger or grab-hopper vessel engaged in dredging or underwater
   *  operations. Default state should be "dredging" — drives the
   *  Rule 27(d) light/shape pattern. */
  "dredger",
  /** Minesweeper / MCM vessel. With state "minesweeping", drives
   *  Rule 27(f) lights and shapes — common Royal Navy presence in
   *  the Solent (Hunt-class, Sandown-class). */
  "minesweeper",
]);
export type VesselType = z.infer<typeof VesselType>;

/**
 * Optional operating-state on a vessel. Drives day-shape and
 * navigation-light selection (Rules 25–30). Default when omitted is
 * "underway" — power-driven sidelights/masthead/stern, no day shape.
 */
export const VesselState = z.enum([
  "underway",
  "anchored",
  "fishing",
  "not-under-command",
  "restricted-in-maneuverability",
  "constrained-by-draught",
  /** Towing astern, tow length ≤ 200m. Standard underway lights plus
   *  two masthead lights forward in vertical line (Rule 24(a)). */
  "towing-astern",
  /** Towing astern, tow length > 200m. As above plus a diamond by
   *  day and an extra yellow tow light over the stern. */
  "towing-long",
  /** Towing alongside or pushing ahead — two masthead lights in
   *  vertical line forward, sidelights, and a stern light or two
   *  aft (Rule 24(c)). */
  "towing-alongside",
  /** Engaged in dredging or underwater operations and unable to get
   *  out of the way. Carries RAM lights (red-white-red) plus two
   *  reds on the obstructed side and two greens on the clear side
   *  (Rule 27(d)). */
  "dredging",
  /** Engaged in mine-clearance operations (Rule 27(f)). Carries
   *  power-driven lights plus three all-round green lights — one
   *  masthead + one at each end of the fore yard. Vessels must keep
   *  1000m astern / 500m on either beam clear. */
  "minesweeping",
  "aground",
  "sail-and-power",
]);
export type VesselState = z.infer<typeof VesselState>;

export const AISInfo = z.object({
  mmsi: z.string().regex(/^\d{9}$/, "MMSI must be 9 digits"),
  name: z.string().min(1).max(20),
  class: z.enum(["A", "B"]),
  destination: z.string().max(20).optional(),
  callsign: z.string().max(7).optional(),
});
export type AISInfo = z.infer<typeof AISInfo>;

export const Waypoint = z.object({
  atSimSeconds: z.number().gte(0),
  position: LatLon,
  cog: BearingDeg,
  sog: SpeedKnots,
});
export type Waypoint = z.infer<typeof Waypoint>;

const VesselCommonFields = {
  id: z.string().min(1),
  vesselType: VesselType,
  rcs: z.number().gte(0).lte(1),
  ais: AISInfo.optional(),
  /** Optional COLREG operating state. Default "underway". */
  state: VesselState.optional(),
};

export const TrackedVessel = z.object({
  kind: z.literal("tracked"),
  ...VesselCommonFields,
  waypoints: z
    .array(Waypoint)
    .min(2, "tracked vessels need at least two waypoints")
    .refine(
      (wps) => wps[0]!.atSimSeconds === 0,
      "first waypoint must be at simSeconds 0",
    )
    .refine((wps) => {
      for (let i = 1; i < wps.length; i++) {
        if (wps[i]!.atSimSeconds <= wps[i - 1]!.atSimSeconds) return false;
      }
      return true;
    }, "waypoints must be in strictly increasing time order"),
});
export type TrackedVessel = z.infer<typeof TrackedVessel>;

export const Behaviour = z.discriminatedUnion("name", [
  z.object({ name: z.literal("follow_lane"), laneId: z.string().min(1) }),
  z.object({
    name: z.literal("colregs_giveway"),
    targetVesselId: z.string().min(1),
  }),
  z.object({
    name: z.literal("random_walk"),
    bounds: BoundingBox,
    speedRangeKnots: z
      .tuple([SpeedKnots, SpeedKnots])
      .refine(([lo, hi]) => lo < hi, "lo must be less than hi"),
  }),
  z.object({
    name: z.literal("fishing_pattern"),
    centre: LatLon,
    radiusNm: z.number().positive(),
    speedKnots: SpeedKnots,
  }),
  /**
   * race-fleet — vessel follows a self-contained course at a fixed
   * speed with optional lateral scatter. `progress` 0..1 picks the
   * starting position along the course at scenario t=0; the vessel
   * continues at speed thereafter, wrapping back to course[0] when
   * it completes the lap (suitable for round-the-island, regatta
   * laps, etc.). All vessels in a fleet can share the same course
   * polyline and differ only in `progress` / `speedKnots` /
   * `lateralOffsetNm`.
   */
  z.object({
    name: z.literal("race-fleet"),
    course: z
      .array(LatLon)
      .min(2, "race-fleet course needs at least two waypoints"),
    speedKnots: SpeedKnots,
    progress: z.number().gte(0).lte(1),
    lateralOffsetNm: z.number().default(0),
  }),
]);
export type Behaviour = z.infer<typeof Behaviour>;

export const BehaviouralVessel = z.object({
  kind: z.literal("behavioural"),
  ...VesselCommonFields,
  spawn: z.object({
    position: LatLon,
    cog: BearingDeg,
    sog: SpeedKnots,
  }),
  behaviour: Behaviour,
});
export type BehaviouralVessel = z.infer<typeof BehaviouralVessel>;

export const Vessel = z.discriminatedUnion("kind", [
  TrackedVessel,
  BehaviouralVessel,
]);
export type Vessel = z.infer<typeof Vessel>;

export const Event = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("rain_cell_appears"), cell: RainCell }),
  z.object({ kind: z.literal("rain_cell_dissipates"), cellId: z.string() }),
  z.object({
    kind: z.literal("sea_state_changes"),
    toState: SeaState,
    transitionSeconds: z.number().gte(0),
  }),
  z.object({
    kind: z.literal("visibility_changes"),
    toNm: z.number().positive(),
    transitionSeconds: z.number().gte(0),
  }),
  z.object({ kind: z.literal("vessel_appears"), vessel: Vessel }),
  z.object({ kind: z.literal("vessel_disappears"), vesselId: z.string() }),
  z.object({
    kind: z.literal("racon_activates"),
    raconId: z.string(),
    durationSeconds: z.number().positive().optional(),
  }),
  z.object({
    kind: z.literal("mob_drop"),
    relativeToOwn: z.object({
      asternNm: z.number().gte(0),
      abeamNm: z.number().gte(0),
      abeamSide: z.enum(["port", "starboard"]),
    }),
  }),
  z.object({
    kind: z.literal("equipment_failure"),
    system: z.enum(["gyro", "antenna", "transmit", "heading-sensor"]),
    mode: z.enum(["fail", "slew", "disagree"]),
    durationSeconds: z.number().positive().optional(),
  }),
]);
export type Event = z.infer<typeof Event>;

export const TimedEvent = z.object({
  atSimSeconds: z.number().gte(0),
  event: Event,
});
export type TimedEvent = z.infer<typeof TimedEvent>;
